import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { fromEvent } from 'rxjs';

import {
  LOADING_LOGO_LETTERS,
  LOADING_LOGO_MARK,
  LOADING_LOGO_VIEW_BOX,
} from './app-loading-logo.geometry';
import {
  LOADING_SIZING,
  LOADING_TIMINGS,
  MARK_INK_DELAY_MS,
  letterUnfoldDelayMs,
  letterUnfoldOffsetUnits,
  resolveSize,
  wordmarkWidthPx,
} from './app-loading.config';
import { AppLoadingService } from './app-loading.service';

/**
 * The blocking loading screen (SB-3521).
 *
 * Two phases share one O: it bounces at page centre while the preload runs,
 * then shrinks and glides into its slot in the wordmark while the other seven
 * letters unfold outward from it. Keeping it as a single element is what makes
 * the landing exact — there is no swap between a standalone mark and a letter.
 *
 * Both phases render the same `<svg>` at the wordmark's viewBox. During the
 * dynamic phase the SVG is scaled up and translated so the O alone sits at
 * centre at its own size; the reveal is then just a transition back to the
 * identity transform, which is why the O lands perfectly in its slot.
 */
@Component({
  selector: 'squadbox-app-loading-screen',
  imports: [TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'fixed inset-0 z-51 grid place-items-center bg-gradient-e-vertical',
    '[class.is-exiting]': "phase() === 'exiting'",
    '[style.--opening-fade]': 'openingFadeMs',
    '[style.--exit-fade]': 'exitFadeMs',
  },
  template: `
    <div
      class="loading-stage"
      [class.is-static]="isStatic()"
      [class.is-reduced]="prefersReducedMotion"
      [style.--mark-scale]="markScale()"
      [style.--mark-shift-x.px]="markShiftX()"
      [style.--settle]="settleMs"
      [style.--intro]="introMs"
      [style.--intro-offset.px]="introOffset"
      [style.--bounce]="bounceMs"
      [style.--bounce-start]="bounceStartMs"
      [style.--unfold]="unfoldMs"
      [style.--ink-fade]="inkFadeMs"
      [style.--ink-delay]="inkDelayMs"
    >
      <svg
        class="loading-logo"
        [attr.viewBox]="viewBox"
        [attr.width]="wordmarkWidth()"
        aria-hidden="true"
      >
        @for (letter of letters; track letter.letter) {
          <path
            class="loading-letter"
            [attr.d]="letter.d"
            [style.--delay]="letterDelayMs(letter)"
            [style.--letter-offset.px]="letterOffsetUnits(letter)"
          />
        }
        <path class="loading-mark" [attr.d]="mark.d" />
      </svg>
    </div>

    <!--
      The logo above is decoration: on a blocking screen the thing worth
      announcing is that the app is loading, not that a wordmark is present.
      Naming the SVG instead would announce "SquadBox, image" and leave a
      screen-reader user with no idea why the app is not there yet.

      Reconciling this with US-4's "no actions, title, or subtext are ever
      displayed on the loading screen": that rule is about failure UI - the
      surrounding scenario is repeated preload failures, and its point is that
      the screen never grows a message or a button when something goes wrong.
      This is not that. It is never displayed, it does not change with state,
      and it is the only thing keeping a blocking overlay from reading as an
      empty, silent app for several seconds. Removing it would satisfy the
      letter of that line and strand every screen-reader user.
    -->
    <p class="sr-only" role="status">{{ 'LOADING.STATUS' | translate }}</p>
  `,
  styles: `
    /*
     * Every duration below is bound as a plain number and multiplied into a
     * time here. Do not "simplify" these to var(--x, 300ms): a custom property
     * holding a unitless 500 substituted straight into a <time> slot is invalid
     * at computed-value time, which silently resolves the whole declaration to
     * 0s — and the var() fallback does NOT rescue it, because the property is
     * set. Every animation and transition on this screen dies, leaving a static
     * indigo O and no wordmark at all.
     */
    :host {
      /*
       * The designer's single ease-out, used for the entrance, the settle and
       * the unfold alike - one curve is what makes the three beats read as one
       * movement rather than three.
       */
      --loading-ease-out: cubic-bezier(0.22, 0.9, 0.3, 1);
      /*
       * The screen fades up rather than existing outright, mirroring the exit.
       *
       * The fill mode is backwards, never both: a forwards-filling animation
       * keeps its final opacity of 1 applied for the life of the element, and
       * an animated value outranks the rule below - the exit fade would stop
       * working entirely. With backwards the animated value applies only while
       * it runs, after which the element falls back to its own opacity and the
       * transition takes over. That is also why the opening is an animation and
       * the exit a transition: only one of them has to be reversible.
       */
      animation: loading-open calc(var(--opening-fade, 300) * 1ms) ease-out
        backwards;
      transition: opacity calc(var(--exit-fade, 300) * 1ms) ease-in-out;
    }
    :host(.is-exiting) {
      opacity: 0;
    }

    @keyframes loading-open {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    .loading-stage {
      /* The whole block enters together: a small lift plus a fade. */
      animation: loading-intro calc(var(--intro, 800) * 1ms)
        var(--loading-ease-out) both;
    }

    .loading-logo {
      display: block;
      overflow: visible;
      /*
       * Dynamic phase: blow the wordmark up so the O alone reads at its own
       * size, and slide the O's centre to the stage centre. The reveal is the
       * transition back to the identity transform, so the O necessarily lands
       * in its slot.
       */
      transform: translateX(var(--mark-shift-x)) scale(var(--mark-scale));
      transform-origin: center;
      transition: transform calc(var(--settle, 500) * 1ms)
        var(--loading-ease-out);
    }
    .is-static .loading-logo {
      transform: none;
    }

    .loading-letter {
      fill: var(--color-neutral-900);
      opacity: 0;
      transform-box: fill-box;
    }
    .is-static .loading-letter {
      animation: loading-unfold calc(var(--unfold, 800) * 1ms)
        var(--loading-ease-out) both;
      animation-delay: calc(var(--delay) * 1ms);
    }

    .loading-mark {
      /* Loading indigo, resolving to brand ink in step with the unfold. */
      fill: #7579ff;
      /*
       * No timing function in the shorthand: every keyframe below sets its own,
       * which is what gives the hop its weight. Naming one here would override
       * only the frames that do not, so the default (ease) is deliberate.
       */
      animation: loading-bounce calc(var(--bounce, 1500) * 1ms) ease infinite;
      /*
       * Wait for the screen to arrive before the first hop, not for the whole
       * logo entrance. Starting at 0s meant the O was squashing while it was
       * still fading up, which reads as abrupt; hanging it off the intro
       * instead put the first frame at 800ms, at or past the reveal for any
       * load the anti-blink floor governs, so the hop never drew at all. The
       * opening fade is the shorter of the two and is what "the screen is
       * here" actually means. Must come after the shorthand, which would
       * otherwise reset the delay to 0.
       */
      animation-delay: calc(var(--bounce-start, 300) * 1ms);
      transform-box: fill-box;
      transform-origin: center bottom;
      /*
       * Eases the O back to rest when the bounce is removed at the reveal,
       * instead of snapping from wherever in the hop it happened to be.
       */
      transition: transform 180ms ease-out;
    }
    .is-static .loading-mark {
      animation: none;
      fill: var(--color-neutral-900);
      transition:
        fill calc(var(--ink-fade, 900) * 1ms) ease
          calc(var(--ink-delay, 650) * 1ms),
        transform 180ms ease-out;
    }

    @keyframes loading-intro {
      from {
        opacity: 0;
        transform: translateY(var(--intro-offset, 12px));
      }
      to {
        opacity: 1;
        transform: none;
      }
    }

    /*
     * The designer's cycle, frame for frame: a restful little hop, then a long
     * breath. The motion is done by 55% and the remaining 45% of the cycle is
     * held at rest - that pause is the effect, not dead time, and shortening it
     * turns the hop into a jitter.
     */
    @keyframes loading-bounce {
      0%,
      55%,
      100% {
        transform: translateY(0) scale(1, 1);
        animation-timing-function: ease-in-out;
      }
      /* anticipation squash */
      8% {
        transform: translateY(0) scale(1.08, 0.92);
        animation-timing-function: ease-out;
      }
      /* apex stretch */
      24% {
        transform: translateY(-14%) scale(0.96, 1.06);
        animation-timing-function: ease-in;
      }
      /* landing squash */
      38% {
        transform: translateY(0) scale(1.12, 0.88);
        animation-timing-function: ease-out;
      }
      /* rebound settle */
      47% {
        transform: translateY(0) scale(0.99, 1.03);
      }
    }

    @keyframes loading-unfold {
      from {
        opacity: 0;
        transform: translateX(var(--letter-offset, 0px));
      }
      to {
        opacity: 1;
        transform: none;
      }
    }

    /*
     * Reduced motion: no bounce, no unfold, no lift. The complete logo is
     * simply present, and the hold and exit fade still play — same information,
     * same timing envelope, nothing that can trigger vestibular symptoms.
     *
     * The spec says nothing about this path; it was designed here and has since
     * been confirmed with design rather than left as an implementer's guess.
     * It is not on the spec page at all, so add it when syncing.
     */
    /*
     * The first selector is NOT ".is-reduced .loading-stage". The class is
     * bound on the stage itself, so a descendant selector asks for a stage
     * inside a stage and never matches - which left the 800ms entrance lift
     * playing for exactly the people who asked for no motion. The three that
     * follow are genuine descendants.
     */
    .loading-stage.is-reduced,
    .is-reduced .loading-logo,
    .is-reduced .loading-letter,
    .is-reduced .loading-mark {
      animation: none;
      transition: none;
    }
    .is-reduced .loading-letter {
      opacity: 1;
    }
    /*
     * Killing the animation is not enough on its own. The dynamic transform is
     * a plain declaration, cleared only by ".is-static", so without this the
     * wordmark spent the whole floor blown up 2.3-2.9x and pushed off-centre,
     * then jumped to its real position with the transition removed.
     */
    .is-reduced .loading-logo {
      transform: none;
    }
    /* "The complete logo is simply present" - including its final colour. */
    .is-reduced .loading-mark {
      fill: var(--color-neutral-900);
    }
  `,
})
export class AppLoadingScreenComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly loading = inject(AppLoadingService);

  protected readonly letters = LOADING_LOGO_LETTERS;
  protected readonly mark = LOADING_LOGO_MARK;
  protected readonly viewBox = `0 0 ${LOADING_LOGO_VIEW_BOX.width} ${LOADING_LOGO_VIEW_BOX.height}`;

  protected readonly settleMs = LOADING_TIMINGS.markSettleMs;
  protected readonly introMs = LOADING_TIMINGS.introMs;
  protected readonly introOffset = LOADING_TIMINGS.introOffsetPx;
  protected readonly bounceMs = LOADING_TIMINGS.bounceCycleMs;
  protected readonly bounceStartMs = LOADING_TIMINGS.bounceStartMs;
  protected readonly unfoldMs = LOADING_TIMINGS.letterUnfoldDurationMs;
  protected readonly inkFadeMs = LOADING_TIMINGS.markInkFadeMs;
  protected readonly inkDelayMs = MARK_INK_DELAY_MS;
  protected readonly openingFadeMs = LOADING_TIMINGS.openingFadeMs;
  protected readonly exitFadeMs = LOADING_TIMINGS.exitFadeMs;

  protected readonly phase = this.loading.phase;
  protected readonly prefersReducedMotion = this.loading.prefersReducedMotion;
  protected readonly isStatic = computed(
    () => this.phase() === 'static' || this.phase() === 'exiting',
  );

  /** Stage width; the host is `fixed inset-0`, so this is the viewport width. */
  private readonly stageWidth = signal(window.innerWidth);

  protected readonly wordmarkWidth = computed(() =>
    wordmarkWidthPx(resolveSize(this.stageWidth(), LOADING_SIZING.wordmark)),
  );

  /** How much to blow the wordmark up so the O reads at its own spec'd size. */
  protected readonly markScale = computed(() => {
    const markTarget = resolveSize(this.stageWidth(), LOADING_SIZING.mark);
    const markRendered =
      (this.mark.width / LOADING_LOGO_VIEW_BOX.width) * this.wordmarkWidth();
    return markRendered === 0 ? 1 : markTarget / markRendered;
  });

  /**
   * Slide the O's centre onto the stage centre.
   *
   * `transform: translateX(T) scale(S)` applies right-to-left — the scale
   * happens FIRST — so a point sitting `d` px from the transform origin ends up
   * at `centre + S*d + T`. The translate therefore has to be expressed in
   * post-scale pixels: `T = -S*d`. Using the raw `-d` leaves the O off-centre by
   * `d*(S-1)`, which at the 80px mark size is about 130px to the right.
   */
  protected readonly markShiftX = computed(() => {
    const unitsToPx = this.wordmarkWidth() / LOADING_LOGO_VIEW_BOX.width;
    const offsetFromCentre =
      this.mark.centerX * unitsToPx - this.wordmarkWidth() / 2;
    return -offsetFromCentre * this.markScale();
  });

  constructor() {
    fromEvent(window, 'resize')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.stageWidth.set(window.innerWidth));
  }

  protected letterDelayMs(
    letter: (typeof LOADING_LOGO_LETTERS)[number],
  ): number {
    return letterUnfoldDelayMs(letter, this.wordmarkWidth());
  }

  protected letterOffsetUnits(
    letter: (typeof LOADING_LOGO_LETTERS)[number],
  ): number {
    return letterUnfoldOffsetUnits(letter, this.wordmarkWidth());
  }
}
